import { useEffect, useState, useCallback } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Mail, Eye, MousePointerClick, AlertTriangle, TrendingUp, RefreshCw, Send, Loader2, Inbox } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

/**
 * LastCampaignCard — widget que muestra las últimas campañas de email con CTR/aperturas
 * en tiempo real via Resend webhooks + CTA "Reenviar a no abiertos".
 *
 * Props: restaurantId, onAction (callback tras un resend exitoso)
 */

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function MiniKpi({ label, value, suffix, Icon, color = "text-foreground" }) {
  return (
    <div className="flex items-start gap-2">
      <div className={`w-7 h-7 rounded-lg bg-muted flex items-center justify-center shrink-0 ${color}`}>
        <Icon className="w-3.5 h-3.5" />
      </div>
      <div>
        <p className="text-lg font-bold leading-tight">{value}{suffix && <span className="text-xs font-medium text-muted-foreground ml-0.5">{suffix}</span>}</p>
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      </div>
    </div>
  );
}

export default function LastCampaignCard({ restaurantId, onAction }) {
  const [campaigns, setCampaigns] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(null); // campaign_id being resent

  const load = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const r = await api.listLoyaltyCampaigns(restaurantId, { limit: 5 });
      setCampaigns(r.campaigns || []);
    } catch (err) {
      toast.error(err.message || "No se pudieron cargar las campañas");
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => { load(); }, [load]);

  const handleResend = async (campaignId) => {
    if (!window.confirm("Se reenviará el email a los destinatarios que aún no lo han abierto. ¿Continuar?")) return;
    setResending(campaignId);
    try {
      const r = await api.resendCampaignToNonOpeners(restaurantId, campaignId);
      if (!r.resend_configured) {
        toast.warning(`Resend no configurado. Se registró el reenvío a ${r.non_openers} contactos pero no se envió ningún email.`);
      } else {
        toast.success(`Reenviado a ${r.sent}/${r.non_openers} destinatarios sin abrir`);
      }
      onAction?.(r);
      load();
    } catch (err) {
      toast.error(err.message || "Error al reenviar la campaña");
    } finally {
      setResending(null);
    }
  };

  if (loading) {
    return (
      <Card className="border-none shadow-soft">
        <CardContent className="p-6 flex items-center justify-center min-h-[120px]">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!campaigns || campaigns.length === 0) {
    return (
      <Card className="border-dashed border-2">
        <CardContent className="p-6 text-center" data-testid="campaigns-empty">
          <Inbox className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">No has enviado ninguna campaña aún. Usa <b>Enviar campaña</b> desde la pestaña Clientes para empezar.</p>
        </CardContent>
      </Card>
    );
  }

  const [last, ...rest] = campaigns;
  const s = last.stats || {};

  return (
    <div className="space-y-3" data-testid="last-campaign-card">
      {/* Last campaign hero */}
      <Card className="border-none shadow-soft overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-violet-500 via-fuchsia-500 to-amber-400" />
        <CardContent className="p-5 sm:p-6">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-lg bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                  <Mail className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                </div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Última campaña enviada</p>
                {last.is_resend && <Badge variant="secondary" className="text-[10px]">Reenvío</Badge>}
              </div>
              <h3 className="font-heading text-base sm:text-lg font-semibold truncate" title={last.subject}>{last.subject}</h3>
              <p className="text-xs text-muted-foreground">{formatDate(last.created_at)} · {last.recipients_count} destinatario{last.recipients_count === 1 ? "" : "s"}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={load}
                title="Actualizar estadísticas"
                data-testid="campaigns-refresh-btn"
              >
                <RefreshCw className="w-3.5 h-3.5" />
              </Button>
              <Button
                size="sm"
                onClick={() => handleResend(last.campaign_id)}
                disabled={resending === last.campaign_id || (s.sends || 0) - (s.opened || 0) === 0 || !last.resend_configured}
                data-testid="campaign-resend-btn"
                title={!last.resend_configured
                  ? "Resend no estaba configurado al enviar la campaña original"
                  : (s.sends || 0) - (s.opened || 0) === 0
                    ? "Todos los destinatarios ya abrieron el email"
                    : "Reenviar a los que aún no abrieron"}
              >
                {resending === last.campaign_id
                  ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  : <Send className="w-3.5 h-3.5 mr-1.5" />}
                Reenviar a no abiertos
                {(s.sends || 0) > 0 && (
                  <span className="ml-1 text-xs opacity-80">({Math.max(0, (s.sends || 0) - (s.opened || 0))})</span>
                )}
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <MiniKpi label="Enviados"    value={s.sends || 0}     Icon={Send} />
            <MiniKpi label="Entregados"  value={s.delivered || 0} Icon={Inbox} color="text-muted-foreground" />
            <MiniKpi label="Apertura"    value={s.open_rate_pct ?? 0}  suffix="%" Icon={Eye} color="text-emerald-700" />
            <MiniKpi label="Clics"       value={s.click_rate_pct ?? 0} suffix="%" Icon={MousePointerClick} color="text-violet-600" />
            <MiniKpi label="Rebotes"     value={(s.bounced || 0) + (s.complained || 0)} Icon={AlertTriangle} color="text-rose-700" />
          </div>

          {!last.resend_configured && (
            <p className="mt-4 text-xs text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" />
              Esta campaña se registró sin Resend configurado — las estadísticas de apertura/clics no están disponibles.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Older campaigns — compact */}
      {rest.length > 0 && (
        <Card className="border-none shadow-soft">
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-3 flex items-center gap-1.5">
              <TrendingUp className="w-3 h-3" /> Historial reciente
            </p>
            <div className="divide-y divide-border">
              {rest.map((c) => {
                const cs = c.stats || {};
                return (
                  <div key={c.campaign_id} className="flex items-center gap-3 py-2 text-sm" data-testid={`campaign-row-${c.campaign_id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate" title={c.subject}>{c.subject}</p>
                      <p className="text-xs text-muted-foreground">{formatDate(c.created_at)} · {c.recipients_count} destinatarios</p>
                    </div>
                    <div className="flex items-center gap-3 text-xs shrink-0">
                      <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400"><Eye className="w-3 h-3" /> {cs.open_rate_pct ?? 0}%</span>
                      <span className="inline-flex items-center gap-1 text-violet-700 dark:text-violet-400"><MousePointerClick className="w-3 h-3" /> {cs.click_rate_pct ?? 0}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
